import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Info, Plus, Trash2, X, Save } from "lucide-react";
import { api, getErrorMessage } from "../../api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import ConfirmDialog from "../../components/ConfirmDialog";
import { validateTierRows, hasFixedRows } from "../../utils/tierValidation";

export default function TierTemplates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);  // { id?, name, rows: [] }
  const [confirmId, setConfirmId] = useState(null);

  const load = () => api.get("/tier-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", rows: [{ min_qty: 1, max_qty: null, percent_off: 0, fixed_price: null }] });
  const startEdit = (t) => setEditing(JSON.parse(JSON.stringify(t)));

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required"); return; }
    const validation = validateTierRows(editing.rows);
    if (!validation.ok) {
      const first = validation.errors[0];
      toast.error(first.index === -1 ? first.message : `Row ${first.index + 1}: ${first.message}`);
      return;
    }
    const body = {
      name: editing.name.trim(),
      rows: editing.rows.map((r) => ({
        min_qty: Number(r.min_qty) || 1,
        max_qty: r.max_qty === "" || r.max_qty === null || r.max_qty === undefined ? null : Number(r.max_qty),
        percent_off: r.percent_off === "" || r.percent_off === null || r.percent_off === undefined ? null : Number(r.percent_off),
        fixed_price: r.fixed_price === "" || r.fixed_price === null || r.fixed_price === undefined ? null : Number(r.fixed_price),
      })),
    };
    try {
      if (editing.id) await api.put(`/tier-templates/${editing.id}`, body);
      else await api.post("/tier-templates", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (err) { toast.error(getErrorMessage(err, "Save failed")); }
  };

  const remove = async (id) => {
    try { await api.delete(`/tier-templates/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(getErrorMessage(err, "Cannot delete")); }
    finally { setConfirmId(null); }
  };

  return (
    <>
      <PageHeader title="Tier Templates" subtitle="Reusable quantity-tier rules. Editing a template instantly affects all sizes using it." testid="page-tiers"
        actions={<Button data-testid="tier-new-btn" onClick={startNew} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white"><Plus size={14} /> New template</Button>} />
      <div className="p-6 grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-6">
        <div className="bg-white border border-[#E2E4E0] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-left px-3 py-2 db-h font-semibold">Name</th>
                <th className="text-left px-3 py-2 db-h font-semibold">Rows</th>
                <th className="text-right px-3 py-2 db-h font-semibold">Used by</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-[#5C665A]" data-testid="tier-empty">No templates.</td></tr>}
              {items.map((t) => (
                <tr key={t.id} className="border-t border-[#E2E4E0] db-row" data-testid={`tier-row-${t.id}`}>
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2 text-xs text-[#5C665A]">
                    {t.rows.map((r, i) => (
                      <Badge key={i} variant="secondary" className="mr-1 mb-0.5">
                        {r.min_qty}-{r.max_qty ?? "∞"} · {r.fixed_price != null ? `€${r.fixed_price}` : `-${r.percent_off ?? 0}%`}
                      </Badge>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-right db-mono" data-testid={`tier-usage-${t.id}`}>{t.usage_count}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button className="text-[#2D6A4F] hover:bg-[#E9F5EC] inline-flex items-center px-2 h-7 rounded-md" onClick={() => startEdit(t)} data-testid={`tier-edit-${t.id}`}>Edit</button>
                    <button className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center px-1.5 h-7 rounded-md ml-1" onClick={() => setConfirmId(t.id)} data-testid={`tier-delete-${t.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {editing && (
          <TierEditor
            editing={editing}
            setEditing={setEditing}
            onSave={save}
          />
        )}
      </div>
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}
        title="Delete template?" description="Templates in use cannot be deleted."
        confirmLabel="Delete" destructive onConfirm={() => remove(confirmId)} testid="tier-confirm-delete" />
    </>
  );
}

function TierEditor({ editing, setEditing, onSave }) {
  const validation = useMemo(() => validateTierRows(editing.rows), [editing.rows]);
  const errByRow = useMemo(() => {
    const m = {};
    for (const e of validation.errors) {
      if (e.index >= 0) (m[e.index] ||= []).push(e.message);
    }
    return m;
  }, [validation]);
  const globalErrors = validation.errors.filter((e) => e.index === -1);
  const showFixedHint = hasFixedRows(editing.rows);

  const suggestedMin = (idx) => {
    if (idx === 0) return 1;
    const prev = editing.rows[idx - 1];
    if (!prev) return 1;
    const pm = prev.max_qty === "" || prev.max_qty == null ? null : Number(prev.max_qty);
    return pm == null ? 1 : pm + 1;
  };

  const addRow = () => {
    const idx = editing.rows.length;
    // Close the previous row if it's ∞ so the new one has a contiguous slot.
    const rows = [...editing.rows];
    if (rows.length > 0 && (rows[idx - 1].max_qty == null || rows[idx - 1].max_qty === "")) {
      rows[idx - 1] = { ...rows[idx - 1], max_qty: Number(rows[idx - 1].min_qty || 1) };
    }
    rows.push({ min_qty: suggestedMin(idx), max_qty: null, percent_off: 0, fixed_price: null });
    setEditing({ ...editing, rows });
  };

  return (
    <div className="bg-white border border-[#E2E4E0] rounded-lg p-4" data-testid="tier-editor">
      <div className="flex items-center justify-between mb-3">
        <h3 className="db-h font-semibold">{editing.id ? "Edit template" : "New template"}</h3>
        <button onClick={() => setEditing(null)} className="text-[#5C665A] hover:text-[#1A1D1A]" data-testid="tier-editor-close"><X size={16} /></button>
      </div>
      <label className="text-xs text-[#5C665A]">Name</label>
      <Input data-testid="tier-editor-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="h-9 mb-3" />
      <div className="border border-[#E2E4E0] rounded-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#F9F9F8] text-[#5C665A]">
              <tr>
                <th className="text-right px-2 py-1.5 db-h font-semibold">Min</th>
                <th className="text-right px-2 py-1.5 db-h font-semibold">Max</th>
                <th className="text-left px-2 py-1.5 db-h font-semibold">Type</th>
                <th className="text-right px-2 py-1.5 db-h font-semibold">Value</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {editing.rows.map((r, idx) => (
                <TierRowEdit
                  key={idx} row={r} idx={idx}
                  rowErrors={errByRow[idx]}
                  onChange={(patch) => {
                    const next = [...editing.rows];
                    next[idx] = { ...r, ...patch };
                    setEditing({ ...editing, rows: next });
                  }}
                  onRemove={() => setEditing({ ...editing, rows: editing.rows.filter((_, i) => i !== idx) })}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {globalErrors.length > 0 && (
        <div className="mt-2 text-xs text-[#C94A4A] inline-flex items-center gap-1" data-testid="tier-global-error">
          <AlertCircle size={12} /> {globalErrors[0].message}
        </div>
      )}
      {showFixedHint && (
        <div className="mt-2 text-xs text-[#5C665A] bg-[#FBF7E6] border border-[#EFE1A6] rounded-md px-3 py-2 inline-flex items-start gap-2" data-testid="tier-fixed-hint">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>
            <b>Fixed prices are final</b> — they replace the base price and
            <b> skip country adjustments</b>. Buyer-personal discount still applies.
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mt-3">
        <button data-testid="tier-add-row" onClick={addRow} className="text-sm text-[#2D6A4F] hover:text-[#1B4332] inline-flex items-center gap-1">
          <Plus size={14} /> Add row
        </button>
        <Button data-testid="tier-save-btn" onClick={onSave} disabled={!validation.ok} className="h-9 bg-[#2D6A4F] hover:bg-[#1B4332] text-white disabled:opacity-50" title={!validation.ok ? "Fix range errors first" : ""}>
          <Save size={14} /> Save
        </Button>
      </div>
    </div>
  );
}

function TierRowEdit({ row, idx, rowErrors, onChange, onRemove }) {
  const type = row.fixed_price !== null && row.fixed_price !== undefined && row.fixed_price !== "" ? "fixed" : "percent";
  return (
    <>
      <tr className={`border-t border-[#E2E4E0] ${rowErrors ? "bg-[#FDF3F3]" : ""}`} data-testid={`tier-row-edit-${idx}`}>
        <td className="px-2 py-1"><Input className="h-8 text-right" type="number" value={row.min_qty ?? 1} onChange={(e) => onChange({ min_qty: e.target.value })} data-testid={`tier-min-${idx}`} /></td>
        <td className="px-2 py-1"><Input className="h-8 text-right" type="number" placeholder="∞" value={row.max_qty ?? ""} onChange={(e) => onChange({ max_qty: e.target.value === "" ? null : e.target.value })} data-testid={`tier-max-${idx}`} /></td>
        <td className="px-2 py-1">
          <Select value={type} onValueChange={(v) => {
            if (v === "percent") onChange({ fixed_price: null, percent_off: row.percent_off ?? 0 });
            else onChange({ percent_off: null, fixed_price: row.fixed_price ?? 0 });
          }}>
            <SelectTrigger className="h-8" data-testid={`tier-type-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">% off</SelectItem>
              <SelectItem value="fixed">Fixed €</SelectItem>
            </SelectContent>
          </Select>
        </td>
        <td className="px-2 py-1">
          {type === "percent" ? (
            <Input className="h-8 text-right" type="number" step="0.1" value={row.percent_off ?? 0} onChange={(e) => onChange({ percent_off: e.target.value })} data-testid={`tier-pct-${idx}`} />
          ) : (
            <Input className="h-8 text-right" type="number" step="0.01" value={row.fixed_price ?? 0} onChange={(e) => onChange({ fixed_price: e.target.value })} data-testid={`tier-fixed-${idx}`} />
          )}
        </td>
        <td className="px-2 py-1 text-right">
          <button onClick={onRemove} className="text-[#C94A4A] hover:bg-[#FDF3F3] inline-flex items-center h-7 w-7 rounded-md justify-center" data-testid={`tier-remove-row-${idx}`}>
            <X size={14} />
          </button>
        </td>
      </tr>
      {rowErrors && (
        <tr className="bg-[#FDF3F3]" data-testid={`tier-row-error-${idx}`}>
          <td colSpan={5} className="px-2 py-1 text-xs text-[#C94A4A]">
            <AlertCircle size={12} className="inline mr-1" /> {rowErrors.join(" · ")}
          </td>
        </tr>
      )}
    </>
  );
}
